"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { useAuthStore } from "@/stores/authStore";

export type PoStatus =
  | "draft"
  | "sent"
  | "approved"
  | "partially_received"
  | "fully_received"
  | "cancelled";

export type PurchaseOrderRow = {
  id: number;
  po_no: string;
  po_date: string;
  supplier_id: number;
  branch_id: number;
  expected_date?: string | null;
  status: PoStatus;
  grn_id?: number | null;
  grn_status?: string | null;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  notes?: string | null;
};

export type PoItemRow = {
  id: number;
  po_id: number;
  product_id: number;
  product_name: string;
  hsn_code?: string | null;
  qty_ordered: number;
  qty_received: number;
  unit?: string | null;
  rate: number;
  gst_rate: number;
  amount: number;
};

export type PurchaseOrderDetail = PurchaseOrderRow & { items: PoItemRow[] };

export function usePurchaseOrders(params: { branch_id?: number; supplier_id?: number; status?: string }) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["purchase-orders", params],
    queryFn: async () => {
      const { data } = await api.get<{ purchase_orders: PurchaseOrderRow[] }>("/api/purchase-orders", {
        params: {
          branch_id: params.branch_id,
          supplier_id: params.supplier_id,
          status: params.status || undefined,
        },
      });

      return data.purchase_orders;
    },
    enabled: Boolean(token),
  });
}

export function usePurchaseOrder(id: number | null) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["purchase-order", id],
    queryFn: async () => {
      const { data } = await api.get<{ purchase_order: PurchaseOrderDetail }>(`/api/purchase-orders/${id}`);

      return data.purchase_order;
    },
    enabled: Boolean(token) && id != null && id > 0,
  });
}

export function usePurchaseOrderMutations() {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["purchase-orders"] });
    void qc.invalidateQueries({ queryKey: ["purchase-order"] });
    void qc.invalidateQueries({ queryKey: ["grns"] });
  };

  const approve = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ purchase_order: PurchaseOrderDetail }>(
        `/api/purchase-orders/${id}/approve`,
      );

      return data.purchase_order;
    },
    onSuccess: invalidate,
  });

  const send = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ message: string }>(`/api/purchase-orders/${id}/send`);

      return data;
    },
    onSuccess: invalidate,
  });

  const convert = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ grn: { id: number } }>(`/api/purchase-orders/${id}/convert`);

      return data.grn;
    },
    onSuccess: invalidate,
  });

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data } = await api.post<{ purchase_order: PurchaseOrderDetail }>("/api/purchase-orders", body);

      return data.purchase_order;
    },
    onSuccess: invalidate,
  });

  return { approve, send, convert, create };
}
